import React, {type CSSProperties, useMemo} from "react";
import {styled} from "styled-components";

/**
 * ResponsiveImage — renders an <img> that fills its container while preserving aspect ratio.
 *
 * How it works
 * - The wrapper defines the box size (via parent height or CSS `aspect-ratio`).
 * - The image is absolutely positioned and sized to the wrapper with CSS `object-fit`.
 *
 * Typical use
 * 1) Specify a height on the parent, and the image will scale to fit (default: contain):
 *    <div style={{height: 240}}>
 *      <ResponsiveImage src={...} alt="..." />
 *    </div>
 *
 * 2) If you don't have a fixed height parent, set an aspect ratio to reserve vertical space:
 *    <ResponsiveImage src={...} alt="..." ratio="16/9" />
 *
 * 3) To cover the entire box (may crop edges), use fit="cover":
 *    <ResponsiveImage src={...} alt="..." ratio={1} fit="cover" />
 */

export type ObjectFit = "contain" | "cover" | "fill" | "none" | "scale-down";

export interface ResponsiveImageProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  /**
   * CSS object-fit behavior; default: "contain" (scale to fit inside the box, fully visible).
   */
  fit?: ObjectFit;
  /**
   * CSS object-position; where to anchor the image inside the box. Default: "center".
   * Examples: "center", "top", "50% 50%", "center right".
   */
  position?: string;
  /**
   * Optional aspect ratio for the box. Accepts a number (width/height, e.g. 16/9 becomes ~1.777)
   * or a CSS ratio string like "16/9". If omitted, the component relies on parent-provided height.
   */
  ratio?: number | `${number}/${number}`;
  /**
   * Forwarded to the underlying <img>.
   */
  decoding?: "sync" | "async" | "auto";
  loading?: "eager" | "lazy";
  /**
   * Additional props forwarded to <img> element.
   */
  imgProps?: Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src" | "alt" | "style" | "loading" | "decoding">;
}

const Wrapper = styled.div<{$ratio?: string}>`
  position: relative;
  width: 100%;
  /* If ratio provided we reserve vertical space using CSS aspect-ratio */
  ${({$ratio}) => ($ratio ? `aspect-ratio: ${$ratio};` : "")}
  /* Allow shrinking/growing with parent */
  min-width: 0;
`;

const Img = styled.img<{$fit: CSSProperties["objectFit"]; $position: string}>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: ${({$fit}) => $fit};
  object-position: ${({$position}) => $position};
  /* Prevent overflow gaps on some browsers */
  max-width: 100%;
  max-height: 100%;
  display: block;
`;

export const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  src,
  alt,
  className,
  style,
  fit = "contain",
  position = "center",
  ratio,
  decoding = "auto",
  loading = "lazy",
  imgProps
}) => {
  // Normalize ratio to a CSS-acceptable string (either "<number>" or "w/h")
  const normalizedRatio = useMemo(() => {
    if (ratio === undefined || ratio === null) return undefined;
    if (typeof ratio === "number") {
      // CSS aspect-ratio accepts a number which is width/height
      return String(ratio);
    }
    return ratio; // assume already a valid CSS ratio like "16/9"
  }, [ratio]);

  return (
    <Wrapper $ratio={normalizedRatio} className={className} style={style}>
      <Img $fit={fit} $position={position} alt={alt} decoding={decoding} loading={loading} src={src} {...imgProps} />
    </Wrapper>
  );
};

export default ResponsiveImage;
